import { Button, Input } from '@crazyegginc/hatch';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Header } from '../components/ui/Header';

import { ReactComponent as StarIcon } from '@crazyegginc/hatch/dist/images/icon-star-filled.svg';
import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow.svg';

import HeatmapSrc from '/src/assets/images/heatmap.jpg';
import ScrollmapSrc from '/src/assets/images/scrollmap.jpg';
import ConfettiSrc from '/src/assets/images/confetti.jpg';
import OverlaySrc from '/src/assets/images/overlay.jpg';
import { normalizeOnboardingSiteUrl, normalizeUrlWithHttps, withQueryParams } from '../utils';
import { Footer } from '../components/ui/Footer';
import { useOnboardingContext } from '../hooks/useOnboardingContext';
import { useAuthContext } from '/src/hooks';

function hostnameFromOnboardingUrl(value) {
  return normalizeOnboardingSiteUrl(value).split('/')[0];
}

export function OnboardingSetup() {
  const { currentUser } = useAuthContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { siteUrl, heatmapUrl, recommendation } = useOnboardingContext(currentUser.onboarding.flow);
  const [inputUrl, setInputUrl] = useState(heatmapUrl);

  useEffect(() => {
    setInputUrl(heatmapUrl);
  }, [heatmapUrl]);

  const handleClick = useCallback(() => {
    const currentParams = searchParams.toString();
    const nextSearchParams = new URLSearchParams(currentParams);
    const normalizedHeatmapUrl = normalizeUrlWithHttps(inputUrl);
    const trimmedHeatmapUrl = normalizeOnboardingSiteUrl(normalizedHeatmapUrl);
    const hasSiteUrl = Boolean(siteUrl);
    const siteHostname = hostnameFromOnboardingUrl(siteUrl);
    const heatmapHostname = hostnameFromOnboardingUrl(trimmedHeatmapUrl);

    if (trimmedHeatmapUrl) {
      nextSearchParams.set('heatmap_url', trimmedHeatmapUrl);
    } else {
      nextSearchParams.delete('heatmap_url');
    }

    if (!hasSiteUrl || (heatmapHostname && siteHostname !== heatmapHostname)) {
      const fallbackSiteUrl = siteUrl || trimmedHeatmapUrl;

      if (heatmapHostname) {
        nextSearchParams.set('site_url', heatmapHostname);
      } else if (fallbackSiteUrl) {
        nextSearchParams.set('site_url', fallbackSiteUrl);
      }
    }

    if (recommendation) {
      nextSearchParams.set('recommendation', recommendation);
    }

    navigate(withQueryParams('/onboarding/check-site', nextSearchParams.toString()));
  }, [searchParams, inputUrl, navigate, recommendation, siteUrl]);

  return (
    <>
      <Header currentStep={1} totalSteps={3} />

      <div className="mx-auto flex w-full max-w-[720px] flex-col px-6 md:px-0">
        <div className="flex flex-col sm:items-center gap-2 sm:gap-3 sm:self-center">
          <h1 className="!m-0 !mt-6 sm:!mt-0 mb-4 sm:text-center text-2xl sm:text-[28px] font-bold leading-9 sm:leading-[40px] text-black-pearl-500 md:text-[32px] md:leading-[48px]">
            Setup first Heatmap Report
          </h1>
          <p className="sm:text-center text-base sm:text-xl font-normal leading-6 sm:leading-[30px] text-black-pearl-500">
            Enter the URL of the page you want to track
            <span className="hidden text-black-pearl-500 sm:inline"> with a Heatmap Report</span>.
          </p>
        </div>

        <Input
          value={inputUrl}
          onChange={(e) => setInputUrl(e.target.value)}
          placeholder="Enter website URL (e.g. example.com)"
          className="h-[50px] max-w-[720px] w-full placeholder:text-lynch-500 text-base font-normal leading-6 text-black-pearl-500 my-5"
        />

        <div className="flex flex-col max-w-[720px] rounded border border-mystic-500 gap-5 self-stretch items-center p-5 w-full mb-6">
          <div className="flex items-center w-full gap-3">
            <StarIcon className="w-4 h-4 text-carnation-500 fill-current flex-shrink-0 self-start" />
            <span className="text-sm font-normal leading-[21px] text-black-pearl-500">
              Heatmap Reports include your Heatmap, Scrollmap, Confetti, Overlay and List Reports.
            </span>
          </div>

          <div className="flex flex-col sm:flex-row w-full flex-wrap items-center justify-center gap-3 md:justify-between md:gap-0">
            <img
              src={HeatmapSrc}
              alt="Heatmap preview"
              className="w-full sm:w-[159px] sm:h-[111px] object-cover rounded border border-mystic-500"
            />
            <img
              src={ScrollmapSrc}
              alt="Scrollmap preview"
              className="w-full sm:w-[159px] sm:h-[111px] object-cover rounded border border-mystic-500"
            />
            <img
              src={ConfettiSrc}
              alt="Confetti preview"
              className="w-full sm:w-[159px] sm:h-[111px] object-cover rounded border border-mystic-500"
            />
            <img
              src={OverlaySrc}
              alt="Overlay preview"
              className="w-full sm:w-[159px] sm:h-[111px] object-cover rounded border border-mystic-500"
            />
          </div>
        </div>

        <Footer>
          <span className="hidden sm:block">&nbsp;</span>
          <Button
            size="xl"
            className="h-[45px] sm:h-[50px] flex items-center justify-center gap-5 px-5 py-[14px] text-sm sm:text-base font-bold leading-6 w-full sm:w-auto"
            onClick={handleClick}
          >
            <span>Create Heatmap Report</span>
            <ArrowIcon className="w-4 h-4 fill-current" />
          </Button>
        </Footer>
      </div>
    </>
  );
}
